import { createSelector } from 'reselect'
import { isFiat, isCrypto, CurrenciesCrypto, CurrenciesFiat } from '@sparling/financial-common'
import { RootState } from '../types'
import { createEnum, IEnum } from '../../utils/createEnum'

export const allInstrumentsStateSelector = (state: RootState): RootState['instruments']['allInstruments'] =>
  state.instruments.allInstruments

export const allInstrumentsLoadingSelector: (state: RootState) => RootState['instruments']['allInstruments']['status'] =
  createSelector(allInstrumentsStateSelector, (s) => s.status)

export const allInstrumentsDataSelector: (state: RootState) => RootState['instruments']['allInstruments']['data'] =
  createSelector(allInstrumentsStateSelector, (s) => s.data)

const KNOWN_CURRENCIES = Array.from(
  new Set(
    [...Object.values(CurrenciesCrypto), ...Object.values(CurrenciesFiat)].map((currency) =>
      String(currency).toUpperCase(),
    ),
  ),
)

export const availableCryptoCurrenciesSelector: (state: RootState) => string[] = createSelector(
  allInstrumentsDataSelector,
  (instruments): string[] => {
    const cryptoValues = Object.values(CurrenciesCrypto).map((v) => String(v).toUpperCase())

    const supported = new Set<string>()

    instruments.forEach((inst) => {
      const u = inst.underlier || ''
      if (typeof u !== 'string' || u.length < 3) return

      const U = u.toUpperCase()

      const suffix3 = U.slice(-3)
      if (isFiat(suffix3)) {
        const base = U.slice(0, U.length - 3)
        if (isCrypto(base)) supported.add(base)
        return
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const baseCode of cryptoValues) {
        // eslint-disable-next-line
        if (!baseCode) continue
        if (U.startsWith(baseCode)) {
          const quote = U.slice(baseCode.length)
          if (isCrypto(baseCode)) supported.add(baseCode)
          if (quote && isCrypto(quote)) supported.add(quote)
          return
        }
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const c of cryptoValues) {
        // eslint-disable-next-line
        if (!c) continue
        if (U.endsWith(c)) {
          const base = U.slice(0, U.length - c.length)
          if (isCrypto(base)) supported.add(base)
          if (isCrypto(c)) supported.add(c)
          return
        }
      }
    })

    return Array.from(supported)
  },
)

export const availableCryptoCurrenciesEnumSelector: (state: RootState) => IEnum[] = createSelector(
  availableCryptoCurrenciesSelector,
  (currencies): IEnum[] => createEnum(currencies),
)

export const availableCurrenciesSelector: (state: RootState) => string[] = createSelector(
  allInstrumentsDataSelector,
  (instruments): string[] => {
    const supported = new Set<string>()

    instruments.forEach((inst) => {
      const underlier = inst.underlier || ''
      if (typeof underlier !== 'string' || underlier.length < 3) return

      const upperUnderlier = underlier.toUpperCase()

      KNOWN_CURRENCIES.forEach((currency) => {
        if (upperUnderlier.includes(currency)) {
          supported.add(currency)
        }
      })
    })

    return Array.from(supported)
  },
)

export const availableCurrenciesEnumSelector: (state: RootState) => IEnum[] = createSelector(
  availableCurrenciesSelector,
  (currencies): IEnum[] => createEnum(currencies),
)
